import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import API, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * OAuth landing page. Emergent redirects here as
 *   /auth/callback#session_id=<session>
 *
 * We exchange the session_id with our backend, store the JWTs in
 * localStorage, refresh AuthContext, and route the user to their portal.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const sessionId = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    if (!sessionId) {
      setError("Missing session token. Please try signing in again.");
      return;
    }

    (async () => {
      try {
        const { data } = await API.post("/auth/google-callback", { session_id: sessionId });
        localStorage.setItem("access_token", data.access_token);
        localStorage.setItem("refresh_token", data.refresh_token);
        // Refresh context (no race — checkAuth uses the token we just stored)
        await checkAuth();
        // Strip the fragment so a refresh doesn't re-process
        window.history.replaceState(null, "", window.location.pathname);
        const u = data.user;
        if (u?.role === "referrer" && u.referrer_token) {
          navigate(`/referrer-dashboard/${u.referrer_token}`, { replace: true });
        } else {
          navigate("/", { replace: true });
        }
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || "Sign-in failed");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="auth-callback-page">
      <div className="text-center max-w-md">
        {error ? (
          <>
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="auth-callback-error">
              {error}
            </div>
            <button
              onClick={() => navigate("/referrer-login", { replace: true })}
              className="text-sm font-semibold hover:underline"
              style={{ color: "#100f5d" }}
            >
              Back to sign in
            </button>
          </>
        ) : (
          <>
            <div className="w-10 h-10 border-4 border-slate-200 border-t-[#100f5d] rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm text-slate-500" style={{ fontFamily: "Manrope, sans-serif" }}>
              Signing you in…
            </p>
          </>
        )}
      </div>
    </div>
  );
}
